import { useMemo } from 'react';
import queryString from 'query-string';
import useResourceLoader from '../../../util/useResourceLoader';
import fetchApi, { FetchOptions } from '../../../util/fetchApi';
import checkErrors from '../../../api/checkErrors';
import { getObject } from '../../../util/mapObject';
import Jnx, { JnxExpr, MultipleJnxObject, useJnx, useMultipleJnx } from '../../../util/jnx';
import { JSONObject } from '../../../api/types';
import { any_string } from '../../../util/types';
import { AuthContextValue } from '../../../context/AuthContext';

/**
 * Describes a lookup configuration object used to fetch and process data options.
 */
export type LookupObject = {
  api?: APIString,
  entity?: string | null,
  jnx: JnxExpr<any[]>,
  params: InputLookupParams,
  options: FetchOptions,
  filter?: JnxExpr<boolean>,
  bindings?: any
};

/**
 * Represents an API identifier string.
 * Can be an internal API, entity API, external URL, or any string.
 */
export type APIString = `api:${string}` | `entity:${string}` | `${'http' | 'https'}://${string}` | any_string

/**
 * Lookup parameters as a dictionary of keys to LookupParam values.
 */
export type LookupParams = {
  [x in string]: LookupParam 
}

/**
 * Input parameters for lookup.
 * Supports keys that can be either strings or Jnx expression strings starting with 'expr:'.
 * Values are either LookupParam or evaluated Jnx expressions.
 */
export type InputLookupParams = {
  [x in any_string | `expr:${string}`]: (
    x extends `expr:${string}` ? JnxExpr<string | number | boolean> : LookupParam
  ) 
}

/**
 * Represents a lookup parameter, which can be a string or an object with a 'field' property.
 */
export type LookupParam = any_string | { field: string }

/**
 * Returns a stable cache key for a lookup config, or null if the lookup has no API.
 * Used to share pre-fetched options from ArrayFieldTemplate down to child LookupFormFields.
 */
export function getLookupCacheKey(lookup: LookupObject): string | null {
  if (!lookup?.api && !lookup?.entity) return null;
  const api = lookup.api || (lookup.entity ? `entity:${lookup.entity}` : '');
  const jnx = typeof lookup.jnx === 'string' ? lookup.jnx : JSON.stringify(lookup.jnx || '');
  return `${api}|${JSON.stringify(lookup.params || {})}|${jnx}`;
}

/**
 * Builds Jnx instances for a lookup's `expr:`-prefixed params OUTSIDE of a hook context (no
 * useMemo/useMultipleJnx available — e.g. ArrayFieldTemplate's plain-loop pre-fetch effect).
 * Mirrors what `useMultipleJnx` does inside `useOptionsLookup` for the same params.
 */
export function buildParamsJnx(params?: InputLookupParams | null): Record<string, Jnx> {
  const result: Record<string, Jnx> = {};
  if (!params || typeof params !== 'object') return result;

  Object.entries(params).forEach(([key, value]) => {
    if (!key.startsWith('expr:')) return;

    try {
      result[key.replace(/^expr:/, '')] = new Jnx(value as JnxExpr);
    } catch (e) {
      console.error('Invalid expr: lookup param', key, e);
    }
  });

  return result;
}

/**
 * Resolves the cache key an array field's shared lookup fetch is ACTUALLY keyed on right now:
 * the static lookup-config key (see `getLookupCacheKey`) plus the params as resolved against the
 * CURRENT rootFormData. Two rows evaluating this against the same rootFormData snapshot always
 * land on the identical key — that's what lets every row of an array share one fetch. When a
 * param's `expr:` value depends on form data that changes (e.g. a picked reporting period), the
 * resolved key changes too, which the caller (ArrayFieldTemplate) treats as "needs a fresh fetch"
 * — still exactly one fetch, shared by every row, redone only when the resolved params actually
 * change instead of once per row or never again.
 */
export function getResolvedLookupKey(
  lookup: LookupObject,
  rootFormData: JSONObject,
  formContext?: Object,
  formObject?: Object,
  paramsJnx?: Record<string, Jnx>
): string | null {
  const baseKey = getLookupCacheKey(lookup);
  if (!baseKey) return null;

  const jnxMap = paramsJnx ?? buildParamsJnx(lookup?.params);
  const paramsString = computeParamString(rootFormData, lookup?.params, jnxMap, formContext, formObject);

  return `${baseKey}::${paramsString}`;
}

export type UseOptionsLookupParams = {
  lookup: LookupObject,
  rootFormData: JSONObject,
  path: string
  auth: AuthContextValue,
  formContext: Object,
  formObject?: Object,
  skip?: boolean,
  precomputedOptions?: any[] | null,
};

/**
 * React hook to perform a lookup of options based on the provided configuration.
 * It fetches data asynchronously, applies JNX expressions, and filters options if needed.
 *
 * @param lookup - The lookup object containing API, params, JNX expressions, filters, and options.
 * @param rootFormData - The root form data to use for evaluating params and filters.
 * @param path - Data path context used during evaluation.
 * @param skip - When true, skips the fetch (used when the parent array has pre-fetched this lookup).
 * @param precomputedOptions - RAW (unfiltered) options already fetched elsewhere (e.g. the array-level
 *   pre-fetch cache shared across every row). Used in place of this hook's own fetch when `skip` is set,
 *   but still run through `filter` below using the CURRENT `rootFormData` — the shared cache holds one
 *   fetch result, but each row may need a different filtered view of it as the form changes.
 * @returns An object containing the filtered options, a loading flag, and an error object if applicable.
 */
function useOptionsLookup({
  lookup,
  rootFormData,
  path,
  auth,
  formContext,
  formObject,
  skip = false,
  precomputedOptions,
}: UseOptionsLookupParams) {
  const { api: propApi, entity, jnx: apiResponseJnxExpr, params, options: fetchOptions, filter: filterJnxExpr } = lookup || {};

  const apiResponseJnx = useJnx(apiResponseJnxExpr);

  const paramsJnx = useMultipleJnx(params as MultipleJnxObject<string | number | boolean>);
  const filterJnx = useJnx(filterJnxExpr);

  const paramsString = useMemo(() => computeParamString(rootFormData, params, paramsJnx, formContext, formObject), [rootFormData, params, paramsJnx, formContext, formObject]);
  const requiresFetch = !!entity || !!propApi;

  const [_options, loadingOptions, errorLoadingOptions] = useResourceLoader(
    async () => {
      if (skip) return null;
      return loadOptionsData(propApi, apiResponseJnx, paramsString, fetchOptions, entity, path, auth, formContext);
    },
    [skip, propApi, apiResponseJnx, paramsString, fetchOptions, entity, path, formContext]
  );

  const rawOptions = precomputedOptions !== undefined ? precomputedOptions : _options;

  const options = useMemo(() => applyFilterToOptions(
    rawOptions,
    filterJnx,
    rootFormData,
    formContext,
    lookup,
    requiresFetch,
    apiResponseJnx,
    path
  ), [
    rawOptions,
    filterJnx,
    rootFormData,
    formContext,
    requiresFetch,
    apiResponseJnx,
    path,
    formContext,
  ]);

  return {
    options,
    loadingOptions,
    errorLoadingOptions,
  };
}

/**
 * Performs a lookup of options outside of React hooks context.
 * Fetches data, applies JNX expressions, and filters the results.
 *
 * @param lookup - Lookup configuration object.
 * @param rootFormData - Root form data for parameter and filter evaluation.
 * @param path - Data path context.
 * @param formContext - Optional form context for Jnx expression evaluation.
 * @param auth - Auth context, used to resolve the entity API's org unit.
 * @param skipFilter - When true, returns the RAW fetched options without applying `filter`. Used
 *   by the array-level pre-fetch cache: `filter` may depend on rootFormData that changes after the
 *   one-time pre-fetch, so the cache must hold unfiltered data and let each row filter it live
 *   (see `useOptionsLookup`'s `precomputedOptions`) instead of baking in a stale filtered result.
 * @returns A Promise resolving to the (optionally filtered) options array or null.
 */
export async function lookupOptions(
  lookup: LookupObject,
  rootFormData: JSONObject,
  formContext: any,
  path: string,
  auth?: AuthContextValue,
  skipFilter: boolean = false,
) {
  const { api: propApi, entity, jnx: apiResponseJnxExpr, params, options: fetchOptions, filter: filterJnxExpr } = lookup || {};

  const apiResponseJnx = apiResponseJnxExpr ? new Jnx(apiResponseJnxExpr) : null;
  const filterJnx = !skipFilter && filterJnxExpr ? new Jnx(filterJnxExpr) : null;

  // Outside a hook context there's no useMultipleJnx to build these — without them, any `expr:`
  // param would be stringified as a raw object instead of evaluated (see buildParamsJnx).
  const paramsString = computeParamString(rootFormData, params, buildParamsJnx(params), formContext);
  const requiresFetch = !!entity || !!propApi;

  const _options = await loadOptionsData(propApi, apiResponseJnx, paramsString, fetchOptions, entity, path, auth, formContext);

  return applyFilterToOptions(
    _options,
    filterJnx,
    rootFormData,
    formContext,
    lookup,
    requiresFetch,
    apiResponseJnx,
    path
  );
}

/**
 * Computes the API URL and relevant metadata for the request based on the API string and parameters.
 *
 * @param api - The API string specifying the resource.
 * @param paramsString - The query parameters string.
 * @returns A tuple with:
 *   - module name ('api', 'external', or 'lookup'),
 *   - fully constructed URL including parameters,
 *   - an optional function to extract the list of items from the response.
 */
function computeApiUrl(
  api: APIString,
  paramsString: string,
  auth?: AuthContextValue
): [string, string, ((x: any) => any[]) | null] {
  let module = null;
  let listFn = null;

  let m = /^(api|entity):(.+)$/.exec(api);

  if (m) {
    if (m[1] === 'entity') {
      listFn = (x: any) => x.items;
    }

    module = 'api';
    const orgId = auth?.user?.user?.actualOrganizationalUnitId;
    api = m[1] === 'entity' ? `uwe-entities/${m[2]}/list/${orgId}` : m[2];
  } else {
    m = /^https?:\/\/(.+)$/.exec(api);

    if (m) {
      module = 'external';
    } else {
      module = 'lookup';
    }
  }

  const delimiter = api.indexOf('?') > -1 ? '&' : '?';
  const url = `${api}${paramsString ? `${delimiter}${paramsString}` : ''}`;

  return [module, url, listFn];
}

/**
 * Converts lookup parameters into a URL query parameter string.
 * Supports both static values and JNX expression evaluations.
 *
 * @param rootFormData - Root form data used to evaluate expressions or extract field values.
 * @param params - The lookup input parameters.
 * @param paramsJnx - Optional pre-evaluated JNX objects for parameters with expressions.
 * @returns URL-encoded query string representing the parameters.
 */
export function computeParamString(
  rootFormData: JSONObject,
  params: InputLookupParams,
  paramsJnx?: Record<string, Jnx | null>,
  formContext: Object = {},
  formObject?: Object,
) {
  if (!params || typeof params !== 'object') {
    return '';
  }
  let lookupParams: LookupParams | null = null;

  if (rootFormData && paramsJnx && Object.keys(paramsJnx).length > 0) {
    lookupParams = Object.entries(params).reduce((_, [key, value]) => {
      if (key.toString().startsWith('expr:')) {
        const cleanKey = key.replace(/^expr:/, '');

        if (paramsJnx[cleanKey]) {
          const jnx = paramsJnx[cleanKey].eval(rootFormData || {}, '', {
            root: rootFormData,
            formContext,
            formObject,
          });
          _[cleanKey] = jnx;
        }
      } else {
        _[key] = value as LookupParam;
      }

      return _;
    }, {} as Record<string, LookupParam>);

    return Object.entries(lookupParams)
        .map(([key, value]) => {
          const stringV = (
            (rootFormData && value && (typeof value !== 'string') && value.field)
            ? `${getObject(rootFormData, value.field)}`
            : `${value}`
          )

          return value ? `${key}=${encodeURIComponent(stringV)}` : null;
        })
        .filter(stringParam => !!stringParam)
        .join('&');
  }

  if (Object.keys(params).length > 0) {
    return queryString.stringify(params);
  }

  return '';
}

/**
 * Loads options data from the API or entity endpoint, applying JNX expressions if specified.
 *
 * @param propApi - The API string to fetch from.
 * @param apiResponseJnx - Optional JNX expression to transform the API response.
 * @param paramsString - Query parameters string.
 * @param fetchOptions - Fetch options to pass along.
 * @param entity - Optional entity name if fetching from entity API.
 * @param path - Optional data path context for JNX evaluation.
 * @returns A Promise resolving to an array of options or null.
 */
async function loadOptionsData(
  propApi: APIString | undefined,
  apiResponseJnx: Jnx | null,
  paramsString: string,
  fetchOptions: FetchOptions,
  entity?: string | null,
  path?: string | null,
  auth?: AuthContextValue,
  formContext?: Object,
) {
  const api = entity ? `entity:${entity}` : propApi;
  if (!api) return null;
  const [module, url, listFn] = computeApiUrl(api, paramsString, auth);
  let data = await checkErrors(await fetchApi[module].get(url, fetchOptions));

  if (listFn) {
    data = listFn(data);
  }

  if (apiResponseJnx) {
    data = apiResponseJnx.eval(data, '', { dataPath: path, data, formContext });
  }

  return data ? (Array.isArray(data) ? data : [data]) : null;
}

/**
 * Filters and transforms the list of options based on a filter JNX expression.
 * Also optionally re-applies the apiResponseJnx transformation if fetch is not required.
 *
 * @param options - The array of options to filter.
 * @param filterJnx - Optional JNX expression that returns a boolean for filtering.
 * @param rootFormData - Root form data for evaluation context.
 * @param requiresFetch - Whether the options require fetching or not.
 * @param apiResponseJnx - Optional JNX expression to transform the entire options list.
 * @param path - Data path context for JNX evaluation.
 * @returns Filtered and transformed array of options or undefined if no options.
 */
function applyFilterToOptions(
  options: any[] | null | undefined,
  filterJnx: Jnx<boolean> | null,
  rootFormData: JSONObject,
  formContext: Object,
  lookup: LookupObject,
  requiresFetch: boolean,
  apiResponseJnx: Jnx<any[]> | null,
  path: string,
) {
  if (!requiresFetch && apiResponseJnx) {
    options = apiResponseJnx.eval(options, '', {
      ...lookup.bindings,
      dataPath: path,
      data: options,
      rootFormData,
      formContext,
    });
  }

  if (!filterJnx) return options;
  if (!options) return;

  return options.filter(option => filterJnx.eval(option, '', { rootFormData, formContext }));
}

export default useOptionsLookup;
